import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  Chip,
  Checkbox,
  Toolbar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DeleteIcon from "@material-ui/icons/Delete";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon, People as PeopleIcon, FilterList as FilterListIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import CompanyUsersDrawer from "../CompanyUsersDrawer";
import api from "../../services/api";
import { head, isArray, has } from "lodash";
import { useDate } from "../../hooks/useDate";
import ColorModeContext from "../../layout/themeContext";

import moment from "moment";
import { i18n } from "../../translate/i18n";
import {
  planRecurrence,
  planContractValue,
  recurrenceLabel,
} from "../../utils/planMetadata";
import { useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: "2px"
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    // padding: theme.spacing(2), //comentado para retirar o scroll do Empresas
  },
  fullWidth: {
    width: "100%",
  },
  spacedTable: {
    // padding="none" on the Table strips MUI's default cell padding; this
    // re-adds breathing room so dense headers like Funcionários/Ativo/
    // Criada Em don't visually collide. Scoped to this table only —
    // doesn't affect the form's classes.fullWidth usage.
    "& .MuiTableCell-root": {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      whiteSpace: "nowrap",
    },
  },
  bulkToolbar: {
    background: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  tableContainer: {
    width: "100%",
    // overflowX: "scroll",
    // ...theme.scrollbarStyles,
    padding: "2px",
  },
  textfield: {
    width: "100%",
  },
  textRight: {
    textAlign: "right",
  },
  row: {
    // paddingTop: theme.spacing(2),
    // paddingBottom: theme.spacing(2),
  },
  control: {
    // paddingRight: theme.spacing(1),
    // paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    // padding: theme.spacing(1),
  },
  kpiCard: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    minWidth: 130,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  kpiValue: {
    fontSize: "1.6rem",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  kpiSplit: {
    fontSize: "0.78rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginTop: 3,
    lineHeight: 1.3,
  },
  filterField: { minWidth: 140 },
  filterDate: { width: 140 },
  filterDateWide: { width: 180 },
  filterCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  filterCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    backgroundColor: `${theme.palette.primary.main}0F`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  filterCardTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  filterCount: {
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}24`,
  },
  clearFiltersButton: {
    marginLeft: "auto",
    fontSize: "0.75rem",
  },
  filterCardBody: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  filterGroupLabel: {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "flex-end",
  },
  rangeBlock: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1, 0.5),
  },
  rangeBlockLabel: {
    display: "block",
    fontSize: "0.6rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  rangePair: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
  },
  rangeDash: {
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(1.25),
  },
  widePopover: {
    maxWidth: "none !important",
    minWidth: "340px !important",
  },
}));

// Mirrors the AWS Cognito password policy (min 8 chars, 1 uppercase, 1
// number, 1 special char) — same rule as frontend/src/pages/Signup and
// frontend/src/components/UserModal. Required only when creating a new
// company (no id yet); on edit, blank means "don't change the password".
const passwordSpecialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/;
const passwordRules = Yup.string()
  .max(50, "Senha muito longa")
  .test(
    "password-min",
    "A senha deve ter pelo menos 8 caracteres",
    (v) => !v || v.length >= 8
  )
  .matches(/[A-Z]/, {
    message: "A senha deve conter pelo menos 1 letra maiúscula",
    excludeEmptyString: true,
  })
  .matches(/[0-9]/, {
    message: "A senha deve conter pelo menos 1 número",
    excludeEmptyString: true,
  })
  .matches(passwordSpecialCharRegex, {
    message: "A senha deve conter pelo menos 1 caractere especial",
    excludeEmptyString: true,
  });

const CompanySchema = Yup.object().shape({
  password: Yup.string().when("id", {
    is: (id) => !id,
    then: passwordRules.required("Senha é obrigatória para criar uma nova empresa"),
    otherwise: passwordRules,
  }),
});

export function CompanyForm(props) {
  const {
    onSubmit,
    onDelete,
    onCancel,
    initialValue,
    loading,
    onIntegrationsChange,
    platformsCatalog = [],
  } = props;
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  // Integrações liberadas pra empresa. Fora do Formik de propósito: o Salvar
  // do formulário vai por PUT /companies/:id, que é só isAuth, e essa
  // permissão é do super -- grava na mudança, pela rota isSuper própria.
  // Mesma razão pela qual o switch do G-Click nunca foi campo do formulário.
  const [integrations, setIntegrations] = useState([]);
  const [savingIntegrations, setSavingIntegrations] = useState(false);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    password: "",
    generateInvoice: true,
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format(
          "YYYY-MM-DD"
        );
      }
      return {
        ...prev,
        ...initialValue,
      };
    });
    setIntegrations(
      isArray(initialValue?.enabledIntegrations)
        ? initialValue.enabledIntegrations
        : []
    );
  }, [initialValue]);

  // Empresa nova: não existe :id pra rota de integrações, então a escolha vai
  // no corpo do POST /companies (isSuper), e o backend grava junto com a linha
  // de CompaniesSettings, na mesma transação.
  const isCreating = !initialValue.id;

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    console.log("Dados do formulário:", data);
    onSubmit(isCreating ? { ...data, enabledIntegrations: integrations } : data);
    setRecord({ ...initialValue, dueDate: "", generateInvoice: true });
    // Sem isto a próxima empresa nova abre o campo já com a seleção da
    // anterior: `integrations` só volta a sincronizar pelo efeito de
    // `initialValue`, e criar duas seguidas não troca esse objeto.
    if (isCreating) {
      setIntegrations([]);
    }
  };

  const handleChangeIntegrations = async (selected) => {
    const slugs = selected.map((platform) => platform.id);
    const previous = integrations;

    // Criando: só estado local. Não há o que gravar ainda, e a rota
    // /companies/:id/integrations precisa de um id que ainda não existe --
    // quem grava é o Salvar, via handleSubmit.
    if (isCreating) {
      setIntegrations(slugs);
      return;
    }

    // Otimista com rollback: sem desfazer, uma falha de rede deixaria a tela
    // dizendo que a integração está liberada quando não está.
    setIntegrations(slugs);
    setSavingIntegrations(true);
    try {
      await api.put(`/companies/${initialValue.id}/integrations`, {
        integrations: slugs,
      });
      onIntegrationsChange &&
        onIntegrationsChange(initialValue.id, slugs);
      toast.success("Integrações atualizadas.");
    } catch (err) {
      setIntegrations(previous);
      toastError(err);
    } finally {
      setSavingIntegrations(false);
    }
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        validationSchema={CompanySchema}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {({ errors, touched }) => (
          <Form className={classes.fullWidth}>
            <Grid spacing={1} container>
              <Grid xs={12} sm={6} md={3} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.name")}
                  name="name"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.email")}
                  name="email"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  required
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  type="password"
                  label={i18n.t("compaies.table.password")}
                  name="password"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.phone")}
                  name="phone"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="plan-selection">{i18n.t("compaies.table.plan")}</InputLabel>
                  <Field
                    as={Select}
                    id="plan-selection"
                    label={i18n.t("compaies.table.plan")}
                    labelId="plan-selection-label"
                    name="planId"
                    margin="dense"
                    required
                    MenuProps={{ classes: { paper: classes.widePopover } }}
                  >
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id} title={plan.name}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={1} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="status-selection">{i18n.t("compaies.table.active")}</InputLabel>
                  <Field
                    as={Select}
                    id="status-selection"
                    label={i18n.t("compaies.table.active")}
                    labelId="status-selection-label"
                    name="status"
                    margin="dense"
                  >
                    <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                    <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                  </Field>
                </FormControl>
              </Grid>              
              {/* <Grid xs={12} sm={6} md={3} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="payment-method-selection">
                    Método de Pagamento
                  </InputLabel>
                  <Field
                    as={Select}
                    id="payment-method-selection"
                    label="Método de Pagamento"
                    labelId="payment-method-selection-label"
                    name="paymentMethod"
                    margin="dense"
                  >
                    <MenuItem value={"pix"}>PIX</MenuItem>
                  </Field>
                </FormControl>
              </Grid> */}
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.document")}
                  name="document"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              {/* <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="status-selection">Campanhas</InputLabel>
                  <Field
                    as={Select}
                    id="campaigns-selection"
                    label="Campanhas"
                    labelId="campaigns-selection-label"
                    name="campaignsEnabled"
                    margin="dense"
                  >
                    <MenuItem value={true}>Habilitadas</MenuItem>
                    <MenuItem value={false}>Desabilitadas</MenuItem>
                  </Field>
                </FormControl>
              </Grid> */}
              <Grid xs={12} sm={6} md={2} item>
                <FormControl variant="outlined" fullWidth>
                  <Field
                    as={TextField}
                    label={i18n.t("compaies.table.dueDate")}
                    type="date"
                    name="dueDate"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="generate-invoice-selection">
                    Gerar Fatura
                  </InputLabel>
                  <Field
                    as={Select}
                    id="generate-invoice-selection"
                    label="Gerar Fatura"
                    labelId="generate-invoice-selection-label"
                    name="generateInvoice"
                    margin="dense"
                  >
                    <MenuItem value={true}>Sim</MenuItem>
                    <MenuItem value={false}>Não</MenuItem>
                  </Field>
                </FormControl>
              </Grid>

              {/* Integrações liberadas. NÃO é campo do Formik, nos dois modos.
                  Editando: grava sozinho, na hora, pela rota isSuper
                  /companies/:id/integrations -- o Salvar do formulário vai por
                  PUT /companies/:id, que é só isAuth, e essa permissão é do
                  super. Criando: não existe :id, então a escolha fica em
                  estado local e viaja no POST /companies, que já é isSuper.
                  Por isso o campo salva na hora numa empresa que existe e só
                  no Salvar numa empresa nova. */}
              <Grid xs={12} sm={6} md={4} item>
                <Autocomplete
                  multiple
                  size="small"
                  disableCloseOnSelect
                  disabled={savingIntegrations || !platformsCatalog.length}
                  options={platformsCatalog}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, value) => option.id === value.id}
                  value={platformsCatalog.filter((platform) =>
                    integrations.includes(platform.id)
                  )}
                  onChange={(e, selected) => handleChangeIntegrations(selected)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        size="small"
                        label={option.name}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Integrações Habilitadas"
                      variant="outlined"
                      margin="dense"
                      helperText={
                        !platformsCatalog.length
                          ? "Não foi possível carregar o catálogo"
                          : savingIntegrations
                          ? "Salvando..."
                          : isCreating
                          ? "Salvo junto com a empresa"
                          : ""
                      }
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Grid justifyContent="flex-end" spacing={1} container>
                  <Grid xs={4} md={1} item>
                    <ButtonWithSpinner
                      className={classes.fullWidth}
                      style={{ marginTop: 7 }}
                      loading={loading}
                      onClick={() => onCancel()}
                      variant="contained"
                    >
                      {i18n.t("compaies.table.clear")}
                    </ButtonWithSpinner>
                  </Grid>
                  {record.id !== undefined ? (
                    <>
                      <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => onDelete(record)}
                          variant="contained"
                          color="secondary"
                          disabled={record.id === 1}
                        >
                          {i18n.t("compaies.table.delete")}
                        </ButtonWithSpinner>
                      </Grid>
                      {/* <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => handleOpenModalUsers()}
                          variant="contained"
                          color="primary"
                        >
                          {i18n.t("compaies.table.user")}
                        </ButtonWithSpinner>
                      </Grid> */}
                    </>
                  ) : null}
                  <Grid xs={6} md={1} item>
                    <ButtonWithSpinner
                      className={classes.fullWidth}
                      style={{ marginTop: 7 }}
                      loading={loading}
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      {i18n.t("compaies.table.save")}
                    </ButtonWithSpinner>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

// Companies with id=1 (super-admin's own) are not selectable. Backend
// hard-blocks the same id, but hiding the checkbox keeps the UI honest.
const PROTECTED_COMPANY_ID = 1;

export function CompaniesManagerGrid(props) {
  const {
    records,
    onSelect,
    onShowEmployees,
    onRowClick,
    selectedIds = [],
    onToggleOne,
    onToggleAll,
    platformsCatalog = [],
  } = props;
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();
  const { mode } = useContext(ColorModeContext);
  const theme = useTheme();

  const selectableIds = (records || [])
    .map(r => r.id)
    .filter(id => id !== PROTECTED_COMPANY_ID);
  const selectedSet = new Set(selectedIds);
  const selectedCount = selectableIds.filter(id => selectedSet.has(id)).length;
  const allSelected =
    selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const renderEmployeeCell = (row) => {
    const count = Number.isFinite(row.employeeCount) ? row.employeeCount : 0;
    const cap = row?.plan?.users;
    const label = Number.isFinite(cap) && cap > 0 ? `${count} / ${cap}` : `${count}`;
    return (
      <Chip
        size="small"
        icon={<PeopleIcon />}
        label={label}
        clickable
        color="primary"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          if (onShowEmployees) onShowEmployees(row);
        }}
        title={i18n.t("compaies.table.employeesTooltip") || "Ver funcionários"}
      />
    );
  };

  const renderStatus = (row) => {
    return row.status === false ? "Não" : "Sim";
  };

  // Nome do catálogo, com o slug como fallback: uma empresa pode ter guardado
  // um slug que saiu do catálogo depois, e mostrar o slug cru é melhor do que
  // sumir com a linha e fazer parecer que ela não tem integração nenhuma.
  const renderIntegrations = (row) => {
    const slugs = isArray(row.enabledIntegrations) ? row.enabledIntegrations : [];

    if (!slugs.length) {
      return (
        <Typography variant="caption" style={{ color: "rgba(0,0,0,0.5)" }}>
          nenhuma
        </Typography>
      );
    }

    return slugs.map((slug) => {
      const platform = platformsCatalog.find((item) => item.id === slug);
      return (
        <Chip
          key={slug}
          size="small"
          label={platform ? platform.name : slug}
          style={{ marginRight: 4 }}
        />
      );
    });
  };

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderPlanValue = (row) => {
    return row.planId !== null ? row.plan.amount ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00' : "-";
  };

  // const renderCampaignsStatus = (row) => {
  //   if (
  //     has(row, "settings") &&
  //     isArray(row.settings) &&
  //     row.settings.length > 0
  //   ) {
  //     const setting = row.settings.find((s) => s.key === "campaignsEnabled");
  //     if (setting) {
  //       return setting.value === "true" ? "Habilitadas" : "Desabilitadas";
  //     }
  //   }
  //   return "Desabilitadas";
  // };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: "#fffead" };
      }
      if (diff <= 0) {
        return { backgroundColor: "#fa8c8c" };
      }
    }
    return {};
  };
  
  const cellStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" }; // Texto preto para fundo amarelo
      }
      if (diff <= 0) {
        return { color: "#fff" }; // Texto branco para fundo vermelho
      }
    }
    return {};
  };

  const iconStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" }; // Ícone preto para fundo amarelo
      }
    }
    return {};
  };

  return (
    <Paper className={classes.tableContainer}>
      <Table
        className={`${classes.fullWidth} ${classes.spacedTable}`}
        // size="small"
        padding="none"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" style={{ width: "1%" }}>
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={(e) => {
                  if (onToggleAll) onToggleAll(e.target.checked, selectableIds);
                }}
                inputProps={{ "aria-label": "select all" }}
              />
            </TableCell>
            <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.name")}</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.email")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.phone")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.plan")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.employees")}</TableCell>
            {/* <TableCell align="center">Campanhas</TableCell> */}
            <TableCell align="center">Integrações</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.active")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.createdAt")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.generateInvoice")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow
              style={{ ...rowStyle(row), cursor: onRowClick ? "pointer" : undefined }}
              key={key}
              hover
              onClick={() => onRowClick && onRowClick(row)}
            >
              <TableCell
                padding="checkbox"
                style={{ ...cellStyle(row), width: "1%" }}
                onClick={(e) => e.stopPropagation()}
              >
                {row.id !== PROTECTED_COMPANY_ID && (
                  <Checkbox
                    checked={selectedSet.has(row.id)}
                    onChange={() => {
                      if (onToggleOne) onToggleOne(row.id);
                    }}
                    inputProps={{ "aria-label": `select company ${row.id}` }}
                  />
                )}
              </TableCell>
              <TableCell
                style={{...cellStyle(row), width: "1%"}}
                align="center"
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton style={iconStyle(row)} onClick={() => onSelect(row)} aria-label="delete">
                  <EditIcon style={iconStyle(row)} />
                </IconButton>
              </TableCell>
              <TableCell style={cellStyle(row)} align="left">{row.name || "-"}</TableCell>
              <TableCell style={cellStyle(row)} align="left" size="small">{row.email || "-"}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{row.phone || "-"}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{renderPlan(row)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{i18n.t("compaies.table.money")} {renderPlanValue(row)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{renderEmployeeCell(row)}</TableCell>
              {/* Só leitura: a edição é o campo "Integrações Habilitadas" do
                  formulário acima. Esta coluna existe para não perder a visão
                  de conjunto que o switch dava -- dá pra correr o olho e ver
                  quem tem o quê. Sem onClick próprio de propósito: clicar na
                  linha seleciona a empresa, que é justamente o passo para
                  editar. */}
              <TableCell style={cellStyle(row)} align="center">
                {renderIntegrations(row)}
              </TableCell>
              <TableCell style={cellStyle(row)} align="center">{renderStatus(row)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{dateToClient(row.createdAt)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{dateToClient(row.dueDate)}<br /><span>{recurrenceLabel(row.plan)}</span></TableCell>
              <TableCell style={cellStyle(row)} align="center">{datetimeToClient(row.lastLogin)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{row.generateInvoice ? i18n.t("compaies.table.yes") : i18n.t("compaies.table.no")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [employeesDrawerOpen, setEmployeesDrawerOpen] = useState(false);
  const [employeesDrawerCompany, setEmployeesDrawerCompany] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Filters
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPlanId, setFilterPlanId] = useState("");
  const [filterValor, setFilterValor] = useState("");
  const [filterCreatedFrom, setFilterCreatedFrom] = useState("");
  const [filterCreatedTo, setFilterCreatedTo] = useState("");
  const [filterDueDateFrom, setFilterDueDateFrom] = useState("");
  const [filterDueDateTo, setFilterDueDateTo] = useState("");
  const [filterLastLoginFrom, setFilterLastLoginFrom] = useState("");
  const [filterLastLoginTo, setFilterLastLoginTo] = useState("");
  const [filterLastUpdateFrom, setFilterLastUpdateFrom] = useState("");
  const [filterLastUpdateTo, setFilterLastUpdateTo] = useState("");
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [bulkInFlight, setBulkInFlight] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const handleShowEmployees = (company) => {
    setEmployeesDrawerCompany(company);
    setEmployeesDrawerOpen(true);
  };

  const handleCloseEmployeesDrawer = () => {
    setEmployeesDrawerOpen(false);
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Grava direto, sem esperar o Salvar do formulário: é uma permissão do
  // boss, não um campo da empresa, e a rota é outra (isSuper).
  // Mantém os chips da tabela em dia com o campo do formulário, sem
  // recarregar a lista inteira a cada alteração.
  const handleIntegrationsChanged = (companyId, slugs) => {
    setRecords((prev) =>
      prev.map((r) =>
        r.id === companyId ? { ...r, enabledIntegrations: slugs } : r
      )
    );
  };

  const handleToggleAll = (checked, selectableIds) => {
    setSelectedIds(checked ? [...selectableIds] : []);
  };

  const handleClearSelection = () => setSelectedIds([]);

  const selectedCompanies = records.filter((r) => selectedIds.includes(r.id));

  const expectedConfirm =
    i18n.t("compaies.bulkDelete.confirmWord") || "EXCLUIR";
  const canConfirmBulk =
    bulkConfirmText.trim().toUpperCase() === expectedConfirm.toUpperCase();

  const handleOpenBulkDialog = () => {
    setBulkConfirmText("");
    setBulkResult(null);
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    if (bulkInFlight) return;
    setBulkDialogOpen(false);
    setBulkResult(null);
  };

  const handleConfirmBulkDelete = async () => {
    if (!canConfirmBulk || bulkInFlight) return;
    setBulkInFlight(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/companies/bulk-delete", {
        ids: selectedIds,
      });
      setBulkResult(data);
      const succeededCount = (data?.succeeded || []).length;
      const failedCount = (data?.failed || []).length;
      if (failedCount === 0) {
        toast.success(
          i18n.t("compaies.bulkDelete.toastAllOk", { count: succeededCount }) ||
            `${succeededCount} empresa(s) excluída(s) com sucesso`
        );
        setBulkDialogOpen(false);
      } else {
        toast.warning(
          i18n.t("compaies.bulkDelete.toastPartial", {
            ok: succeededCount,
            failed: failedCount,
          }) ||
            `${succeededCount} excluída(s), ${failedCount} falharam`
        );
        // Keep the dialog open so the boss can see which failed and why.
      }
      setSelectedIds([]);
      await loadPlans();
    } catch (e) {
      toast.error(
        e?.response?.data?.error ||
          i18n.t("compaies.bulkDelete.toastError") ||
          "Não foi possível excluir as empresas"
      );
    } finally {
      setBulkInFlight(false);
    }
  };
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    password: "",
    document: "",
    paymentMethod: "",
    generateInvoice: true
  });

  const [platformsCatalog, setPlatformsCatalog] = useState([]);

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catálogo em código (PLATFORMS_CATALOG), não tabela: não dá pra liberar
  // integração que não existe. Buscado aqui, no pai, porque o formulário e a
  // tabela precisam do mesmo mapa slug -> nome. Falha deixa o campo fechado
  // (ver o helperText dele), sem derrubar a tela.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platforms/catalog");
        setPlatformsCatalog(isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      console.log("Lista de empresas carregada:", companyList);
      setRecords(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    console.log("Dados enviados para o backend:", data);
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord((prev) => ({
      ...prev,
      // `id` entrou nesta limpeza junto com o campo de integrações. Antes
      // ficava para trás: o formulário voltava a parecer vazio, mas seguia
      // apontando para a última empresa selecionada. Visualmente não
      // aparecia porque nenhum campo mostra o id -- com o campo de
      // integrações, que grava na hora, isso viraria escrita numa empresa
      // que o usuário não está mais vendo.
      id: undefined,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      // campaignsEnabled: false,
      dueDate: "",
      password: "",
      document: "",
      paymentMethod: "",
      generateInvoice: true,
      enabledIntegrations: []
    }));
  };

  const handleSelect = (data) => {
    // let campaignsEnabled = false;

    // const setting = data.settings.find(
    //   (s) => s.key.indexOf("campaignsEnabled") > -1
    // );
    // if (setting) {
    //   campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    // }

    console.log("Dados da empresa selecionada:", data);
    setRecord((prev) => ({
      ...prev,
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      // campaignsEnabled,
      dueDate: data.dueDate || "",
      password: "",
      document: data.document || "",
      paymentMethod: data.paymentMethod || "",
      generateInvoice: data.generateInvoice !== undefined ? data.generateInvoice : true,
      // Sem isto o campo de integrações abre VAZIO numa empresa que já tem
      // integração liberada -- e aí marcar uma segunda gravaria só ela,
      // revogando silenciosamente a que já existia.
      enabledIntegrations: isArray(data.enabledIntegrations)
        ? data.enabledIntegrations
        : [],
    }));
  };

  // Empresa 1 e a propria casa: nao paga, entao nao entra em nada que seja
  // dinheiro. Mesmo criterio que o ListCompaniesForSuperService ja usa nas
  // queries de fatura (companyId != 1).
  const CASA = 1;

  const kpis = useMemo(() => {
    const active = records.filter((r) => r.status !== false);
    const distinctPlans = new Set(records.map((r) => r.planId).filter(Boolean)).size;

    // Dinheiro: so empresa ativa e pagante. O ciclo e o valor saem do PLANO
    // (plan.recurrence / plan.billingTypes), nunca mais de Company.recurrence.
    const pagantes = active.filter((r) => r.id !== CASA);

    // Os tres cards por ciclo somam o valor de CONTRATO de cada empresa.
    const porCiclo = (recurrence) =>
      pagantes
        .filter((r) => planRecurrence(r.plan) === recurrence)
        .reduce((total, r) => total + planContractValue(r.plan), 0);

    // "Valor Total Mensal" e outra coisa: quanto se espera RECEBER no mes
    // corrente. Plano mensal entra inteiro; semestral/anual entra so se foi
    // mensalizado no cartao, dividido pelo numero de parcelas que o cliente
    // escolheu (asaasInstallmentCount, que e null pra quem pagou o termo
    // inteiro de uma vez); quem paga em boleto unico nao entra.
    const previsaoDoMes = pagantes.reduce((total, r) => {
      const recurrence = planRecurrence(r.plan);
      if (!recurrence) return total;

      const contrato = planContractValue(r.plan);
      if (recurrence === "MENSAL") return total + contrato;

      const parcelas = Number(r.asaasInstallmentCount) || 0;
      if (parcelas >= 2) {
        return total + Math.round((contrato / parcelas) * 100) / 100;
      }
      return total;
    }, 0);

    return {
      total: records.length,
      activeCount: active.length,
      inactiveCount: records.length - active.length,
      distinctPlans,
      previsaoDoMes,
      mensal: porCiclo("MENSAL"),
      semestral: porCiclo("SEMESTRAL"),
      anual: porCiclo("ANUAL"),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const planOptions = useMemo(() => {
    const seen = new Map();
    records.forEach((r) => {
      if (r.plan && !seen.has(r.planId)) seen.set(r.planId, r.plan.name);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [records]);

  const activeFilterCount = [
    filterName, filterEmail, filterPlanId, filterValor,
    filterCreatedFrom, filterCreatedTo, filterDueDateFrom, filterDueDateTo,
    filterLastLoginFrom, filterLastLoginTo, filterLastUpdateFrom, filterLastUpdateTo,
  ].filter(Boolean).length;

  const isInDateRange = (dateVal, from, to) => {
    if (!from && !to) return true;
    if (!dateVal) return false;
    const d = moment(dateVal);
    if (from && d.isBefore(moment(from), "day")) return false;
    if (to && d.isAfter(moment(to), "day")) return false;
    return true;
  };

  const filteredRecords = useMemo(() => {
    return records.filter((row) => {
      if (filterName && !(row.name || "").toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterEmail && !(row.email || "").toLowerCase().includes(filterEmail.toLowerCase())) return false;
      if (filterPlanId && String(row.planId) !== String(filterPlanId)) return false;
      if (filterValor) {
        const amt = row.plan?.amount;
        if (amt === undefined || amt === null) return false;
        if (!String(amt).includes(filterValor)) return false;
      }
      if (!isInDateRange(row.createdAt, filterCreatedFrom, filterCreatedTo)) return false;
      if (!isInDateRange(row.dueDate, filterDueDateFrom, filterDueDateTo)) return false;
      if (!isInDateRange(row.lastLogin, filterLastLoginFrom, filterLastLoginTo)) return false;
      if (!isInDateRange(row.updatedAt, filterLastUpdateFrom, filterLastUpdateTo)) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, filterName, filterEmail, filterPlanId, filterValor,
      filterCreatedFrom, filterCreatedTo, filterDueDateFrom, filterDueDateTo,
      filterLastLoginFrom, filterLastLoginTo, filterLastUpdateFrom, filterLastUpdateTo]);

  const formatBRL = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "R$ 0,00";
    return n.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
  };

  const handleRowClick = (row) => {
    setEmployeesDrawerCompany(row);
    setEmployeesDrawerOpen(true);
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <CompanyForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            onIntegrationsChange={handleIntegrationsChanged}
            platformsCatalog={platformsCatalog}
          />
        </Grid>
        {/* KPI cards */}
        <Grid xs={12} item>
          <Grid container spacing={1}>
            {[
              {
                label: "Total de Empresas",
                value: kpis.total,
                color: "#1565c0",
                subLines: [`${kpis.activeCount} ativas`, `${kpis.inactiveCount} inativas`],
              },
              { label: "Planos Distintos", value: kpis.distinctPlans, color: "#6a1b9a" },
              { label: "Valor Total Mensal", value: formatBRL(kpis.previsaoDoMes), color: "#2e7d32" },
              { label: "Planos Mensais", value: formatBRL(kpis.mensal), color: "#00695c" },
              { label: "Planos Semestrais", value: formatBRL(kpis.semestral), color: "#e65100" },
              { label: "Planos Anuais", value: formatBRL(kpis.anual), color: "#c62828" },
            ].map((k) => (
              <Grid key={k.label} item xs={6} sm={4} md={2}>
                <Paper variant="outlined" className={classes.kpiCard}>
                  <div className={classes.kpiValue} style={{ color: k.color }}>{k.value}</div>
                  <div className={classes.kpiLabel}>{k.label}</div>
                  {k.subLines && (
                    <div className={classes.kpiSplit}>
                      {k.subLines.map(line => <div key={line}>{line}</div>)}
                    </div>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Filters */}
        <Grid xs={12} item>
          <div className={classes.filterCard}>
            <div className={classes.filterCardHeader}>
              <FilterListIcon color="primary" fontSize="small" />
              <Typography className={classes.filterCardTitle}>Filtros</Typography>
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  className={classes.filterCount}
                  label={`${activeFilterCount} ativo${activeFilterCount > 1 ? "s" : ""}`}
                />
              )}
              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  variant="text"
                  color="secondary"
                  className={classes.clearFiltersButton}
                  onClick={() => {
                    setFilterName(""); setFilterEmail(""); setFilterPlanId(""); setFilterValor("");
                    setFilterCreatedFrom(""); setFilterCreatedTo(""); setFilterDueDateFrom(""); setFilterDueDateTo("");
                    setFilterLastLoginFrom(""); setFilterLastLoginTo(""); setFilterLastUpdateFrom(""); setFilterLastUpdateTo("");
                  }}>
                  Limpar filtros
                </Button>
              )}
            </div>
            <div className={classes.filterCardBody}>
              <div>
                <div className={classes.filterGroupLabel}>Busca</div>
                <div className={classes.filterRow}>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Nome da Empresa"
                    className={classes.filterField}
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    label="E-mail"
                    className={classes.filterField}
                    value={filterEmail}
                    onChange={(e) => setFilterEmail(e.target.value)}
                  />
                  <FormControl variant="outlined" size="small" className={classes.filterField}>
                    <InputLabel>Plano</InputLabel>
                    <Select
                      value={filterPlanId}
                      onChange={(e) => setFilterPlanId(e.target.value)}
                      label="Plano"
                      MenuProps={{ classes: { paper: classes.widePopover } }}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {planOptions.map((p) => (
                        <MenuItem key={p.id} value={p.id} title={p.name}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Valor"
                    className={classes.filterDate}
                    value={filterValor}
                    onChange={(e) => setFilterValor(e.target.value.replace(/[^0-9.,]/g, ""))}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </div>
              </div>
              <div>
                <div className={classes.filterGroupLabel}>Período</div>
                <div className={classes.filterRow}>
                  <div className={classes.rangeBlock}>
                    <span className={classes.rangeBlockLabel}>Criada em</span>
                    <div className={classes.rangePair}>
                      <TextField size="small" variant="outlined" label="De" type="date"
                        className={classes.filterDate} InputLabelProps={{ shrink: true }}
                        value={filterCreatedFrom} onChange={(e) => setFilterCreatedFrom(e.target.value)} />
                      <span className={classes.rangeDash}>—</span>
                      <TextField size="small" variant="outlined" label="Até" type="date"
                        className={classes.filterDate} InputLabelProps={{ shrink: true }}
                        value={filterCreatedTo} onChange={(e) => setFilterCreatedTo(e.target.value)} />
                    </div>
                  </div>
                  <div className={classes.rangeBlock}>
                    <span className={classes.rangeBlockLabel}>Vencimento</span>
                    <div className={classes.rangePair}>
                      <TextField size="small" variant="outlined" label="De" type="date"
                        className={classes.filterDate} InputLabelProps={{ shrink: true }}
                        value={filterDueDateFrom} onChange={(e) => setFilterDueDateFrom(e.target.value)} />
                      <span className={classes.rangeDash}>—</span>
                      <TextField size="small" variant="outlined" label="Até" type="date"
                        className={classes.filterDate} InputLabelProps={{ shrink: true }}
                        value={filterDueDateTo} onChange={(e) => setFilterDueDateTo(e.target.value)} />
                    </div>
                  </div>
                  <div className={classes.rangeBlock}>
                    <span className={classes.rangeBlockLabel}>Último login</span>
                    <div className={classes.rangePair}>
                      <TextField size="small" variant="outlined" label="De" type="date"
                        className={classes.filterDate} InputLabelProps={{ shrink: true }}
                        value={filterLastLoginFrom} onChange={(e) => setFilterLastLoginFrom(e.target.value)} />
                      <span className={classes.rangeDash}>—</span>
                      <TextField size="small" variant="outlined" label="Até" type="date"
                        className={classes.filterDate} InputLabelProps={{ shrink: true }}
                        value={filterLastLoginTo} onChange={(e) => setFilterLastLoginTo(e.target.value)} />
                    </div>
                  </div>
                  <div className={classes.rangeBlock}>
                    <span className={classes.rangeBlockLabel}>Última atualização</span>
                    <div className={classes.rangePair}>
                      <TextField size="small" variant="outlined" label="De" type="date"
                        className={classes.filterDateWide} InputLabelProps={{ shrink: true }}
                        value={filterLastUpdateFrom} onChange={(e) => setFilterLastUpdateFrom(e.target.value)} />
                      <span className={classes.rangeDash}>—</span>
                      <TextField size="small" variant="outlined" label="Até" type="date"
                        className={classes.filterDateWide} InputLabelProps={{ shrink: true }}
                        value={filterLastUpdateTo} onChange={(e) => setFilterLastUpdateTo(e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Grid>

        <Grid xs={12} item>
          {selectedIds.length > 0 && (
            <Toolbar
              className={classes.bulkToolbar}
              variant="dense"
            >
              <Typography
                style={{ flex: "1 1 100%" }}
                color="inherit"
                variant="subtitle1"
              >
                {i18n.t("compaies.bulkDelete.selected", {
                  count: selectedIds.length,
                }) || `${selectedIds.length} selecionada(s)`}
              </Typography>
              <Button
                variant="text"
                color="inherit"
                onClick={handleClearSelection}
                style={{ marginRight: 8 }}
              >
                {i18n.t("compaies.bulkDelete.clear") || "Limpar"}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleOpenBulkDialog}
              >
                {i18n.t("compaies.bulkDelete.button") || "Excluir selecionadas"}
              </Button>
            </Toolbar>
          )}
          <CompaniesManagerGrid
            records={filteredRecords}
            onSelect={handleSelect}
            onShowEmployees={handleShowEmployees}
            onRowClick={handleRowClick}
            selectedIds={selectedIds}
            onToggleOne={handleToggleOne}
            onToggleAll={handleToggleAll}
            platformsCatalog={platformsCatalog}
          />
        </Grid>
      </Grid>
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
      <CompanyUsersDrawer
        open={employeesDrawerOpen}
        onClose={handleCloseEmployeesDrawer}
        company={employeesDrawerCompany}
      />
      <Dialog
        open={bulkDialogOpen}
        onClose={handleCloseBulkDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={bulkInFlight}
      >
        <DialogTitle>
          {i18n.t("compaies.bulkDelete.dialogTitle") ||
            "Excluir empresas selecionadas"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            {i18n.t("compaies.bulkDelete.dialogIntro", {
              count: selectedCompanies.length,
            }) ||
              `Você está prestes a excluir ${selectedCompanies.length} empresa(s).`}
          </Typography>
          <ul style={{ maxHeight: 180, overflowY: "auto", paddingLeft: 20 }}>
            {selectedCompanies.map((c) => (
              <li key={c.id}>
                <Typography variant="body2">{c.name}</Typography>
              </li>
            ))}
          </ul>
          <Typography
            variant="body2"
            style={{ color: "#b71c1c", marginTop: 12, fontWeight: 500 }}
          >
            {i18n.t("compaies.bulkDelete.dialogWarning") ||
              "Todos os dados destas empresas (funcionários, atendimentos, mensagens, conexões WhatsApp e assinaturas Asaas) serão excluídos permanentemente. Esta ação não pode ser desfeita."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            variant="outlined"
            label={
              i18n.t("compaies.bulkDelete.confirmInputLabel", {
                word: expectedConfirm,
              }) || `Digite ${expectedConfirm} para confirmar`
            }
            value={bulkConfirmText}
            onChange={(e) => setBulkConfirmText(e.target.value)}
            disabled={bulkInFlight}
          />
          {bulkResult?.failed?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Typography
                variant="subtitle2"
                style={{ color: "#b71c1c" }}
                gutterBottom
              >
                {i18n.t("compaies.bulkDelete.failuresTitle") ||
                  "Falhas durante a exclusão"}
              </Typography>
              <ul style={{ paddingLeft: 20 }}>
                {bulkResult.failed.map((f) => (
                  <li key={f.id}>
                    <Typography variant="body2">
                      {f.name || `#${f.id}`} — {f.error}
                    </Typography>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog} disabled={bulkInFlight}>
            {i18n.t("compaies.bulkDelete.cancel") || "Cancelar"}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="secondary"
            disabled={!canConfirmBulk || bulkInFlight}
            startIcon={
              bulkInFlight ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {bulkInFlight
              ? i18n.t("compaies.bulkDelete.deleting") || "Excluindo..."
              : i18n.t("compaies.bulkDelete.confirm") || "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
